import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fieldOptions, inboxApi, type ContactField } from '@wa/shared';

/**
 * What a client wants to know about a customer beyond a name.
 *
 * The key is derived from the label by the server and never shown: it exists so
 * that CSV headers and segment rules have something stable to point at, and asking
 * somebody to invent an identifier is asking them to invent one with a space in it.
 */
export function FieldsPanel({ fields, onClose }: { fields: ContactField[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['contact-fields'] });

  const create = useMutation({
    mutationFn: inboxApi.createContactField,
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not add that field.'),
  });

  const remove = useMutation({
    mutationFn: inboxApi.deleteContactField,
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not remove that field.'),
  });

  return (
    <Panel title="Contact fields" onClose={onClose}>
      {error && (
        <p role="alert" className="mb-3 rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {fields.length === 0 ? (
        <p className="text-small text-(--color-muted)">
          No fields yet. Add one and it appears on every contact, in the CSV mapping
          screen, and as something a list can be built from.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--color-line-dim)">
          {fields.map((field) => (
            <li key={field.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-small font-medium">{field.label}</p>
                <p className="truncate text-micro text-(--color-muted)">
                  {field.fieldType}
                  {field.fieldType === 'list' && `: ${fieldOptions(field).join(', ')}`}
                </p>
              </div>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(field.id)}
                className="tap shrink-0 rounded-(--radius-field) border border-(--color-line) px-2 py-1 text-micro text-(--color-muted) transition hover:text-(--color-bad) disabled:opacity-40"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-micro leading-relaxed text-(--color-muted)">
        Removing a field hides it. The values stay on every contact, so adding the
        same field back restores them.
      </p>

      <AddField isBusy={create.isPending} onCreate={(f) => create.mutate(f)} />
    </Panel>
  );
}

function AddField({ isBusy, onCreate }: {
  isBusy: boolean;
  onCreate: (field: { label: string; fieldType: string; options?: string[] }) => void;
}) {
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [options, setOptions] = useState('');

  const list = options.split(',').map((o) => o.trim()).filter(Boolean);
  const canSubmit = label.trim().length >= 2 && (fieldType !== 'list' || list.length > 0) && !isBusy;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onCreate({ label: label.trim(), fieldType, options: fieldType === 'list' ? list : undefined });
        setLabel(''); setOptions(''); setFieldType('text');
      }}
      className="mt-4 flex flex-col gap-2 border-t border-(--color-line) pt-4"
    >
      <div className="flex flex-wrap gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="City, Order value, Plan…"
          className="min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
        />
        <select
          value={fieldType}
          onChange={(e) => setFieldType(e.target.value)}
          className="shrink-0 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-2 text-small"
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
          <option value="list">List</option>
          <option value="boolean">Yes / No</option>
        </select>
      </div>

      {fieldType === 'list' && (
        <input
          value={options}
          onChange={(e) => setOptions(e.target.value)}
          placeholder="Gold, Silver, Bronze"
          className="rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
        />
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="tap self-start rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-medium text-white disabled:opacity-40"
      >
        Add field
      </button>
    </form>
  );
}

/** The chrome the two side panels share. */
export function Panel({ title, onClose, children }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-(--color-surface)">
      <header className="flex shrink-0 items-center justify-between border-b border-(--color-line) px-3 py-3 sm:px-4">
        <h2 className="text-body font-semibold">{title}</h2>
        <button
          type="button" onClick={onClose} aria-label="Close"
          className="tap grid size-8 place-items-center rounded-(--radius-field) text-(--color-muted) transition hover:bg-(--color-sunken) hover:text-(--color-ink)"
        >
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
      </header>
      <div className="pane-scroll min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">{children}</div>
    </div>
  );
}
